import { useGetTemplateByIdQuery } from 'store/services/helios/templates';
import { useCallback, useEffect } from 'react';
import { Box, IconButton, MenuItem } from '@mui/material';
import { FieldsValues, FormWrapper, SectionTitle } from './FunctionModals.styles';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Delete, Add } from '@mui/icons-material';
import type { InferType } from 'yup';

import type { ModalsType } from 'types';
import type { FuncParameterType } from 'types/functions';
import { TextField } from 'components/mui/common/TextField';
import { ActionModal } from 'components/mui/common/ActionModal';
import { Button } from 'components/mui/common/Button';
import { Switch } from 'components/mui/common/Switch';
import { Select } from 'components/mui/common/Select';
import { useAddFunctionParametersModalSchema } from 'utils/validations/helios/useHeliosFunctionsSchema';
import { SelectOverviewTemplateModal } from './SelectOverviewTemplateModal';
import { useBooleanState } from 'utils/hooks';
import { LookupInput } from 'components/mui/common/LookupInput';

const parameterTypeOptions: FuncParameterType[] = ['Text', 'MultiSelect', 'Relation', 'Checkbox', 'Date', 'DateTime', 'Time', 'Password', 'File'];

export const ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH = 'helios.functions.addFunctionParametersModal';
export interface FunctionParamsFormValues {
  id?: string | null;
  name: string;
  labelText?: string;
  functionParameterType: string;
  dateFormatVariant?: string | null;
  order?: number;
  isRequired?: boolean;
  isMultiSelect: boolean;
  functionParameterSelectedList:
    | {
        name: string;
        value?: string;
      }[]
    | null;
  templateBrowseId?: string | null;
}

export type AddParametersModalSchema = InferType<ReturnType<typeof useAddFunctionParametersModalSchema>>;
interface AddParametersToFunctionModalProps extends ModalsType {
  onSubmit: (parameter: AddParametersModalSchema) => void;
  editingParameter?: FunctionParamsFormValues | undefined;
  datasourceId?: string;
}

const defaultValues: FunctionParamsFormValues = {
  name: '',
  labelText: '',
  functionParameterType: 'Text',
  dateFormatVariant: 'Date',
  order: 0,
  isRequired: false,
  isMultiSelect: false,
  functionParameterSelectedList: [],
  templateBrowseId: null,
};

export const AddParametersToFunctionModal: React.FC<AddParametersToFunctionModalProps> = ({
  open,
  editingParameter,
  onClose,
  onSubmit,
  datasourceId,
}) => {
  const { t } = useTranslation();
  const parameterSchema = useAddFunctionParametersModalSchema();
  const [isOverviewTemplateOpen, toggleOverviewTemplateOpen] = useBooleanState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(parameterSchema),
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'functionParameterSelectedList',
  });
  const [functionParameterType, templateBrowseId, dateFormatVariant] = useWatch({
    control,
    name: ['functionParameterType', 'templateBrowseId', 'dateFormatVariant'],
  });
  const { currentData: templateData } = useGetTemplateByIdQuery(
    {
      id: templateBrowseId || '',
    },
    {
      skip: !templateBrowseId,
    }
  );

  useEffect(() => {
    if (open && editingParameter) reset(editingParameter);
    else if (open) reset(defaultValues);
  }, [open, editingParameter, reset]);

  const handleAddOption = useCallback(() => {
    append({ name: '', value: '' });
  }, [append]);

  const onSelectOverviewTemplate = useCallback(
    (templateId: string[]) => {
      if (templateId[0]) {
        setValue('templateBrowseId', templateId[0]);
      }
    },
    [setValue]
  );

  return (
    <>
      <ActionModal
        open={open}
        title={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.title`)}
        onClose={onClose}
        onCancel={onClose}
        onSubmit={handleSubmit(onSubmit)}
        isSubmitting={isSubmitting}
        disableBackdropClick={isSubmitting}
        cancelText={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.buttons.cancel`)}
        submitText={
          editingParameter
            ? t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.buttons.update`)
            : t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.buttons.add`)
        }
        submitIcon={{
          icon: <Add color="inherit" />,
          position: 'start',
        }}>
        <FormWrapper>
          <SectionTitle variant="caption">{t('helios.functions.addFunctionModal.fields.sections.basicInfo')}</SectionTitle>

          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                fullWidth
                label={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.name`)}
              />
            )}
          />
          <Controller
            name="labelText"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                fullWidth
                label={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.label`)}
              />
            )}
          />
          <Controller
            name="functionParameterType"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                {...field}
                onChange={(e) => {
                  const selectedType = e.target.value;
                  field.onChange(selectedType);

                  if (selectedType !== 'MultiSelect') setValue('functionParameterSelectedList', []);

                  const allowedVariants =
                    selectedType === 'Date' ? ['Date', 'MonthYear'] : selectedType === 'DateTime' ? ['DateTime', 'Date', 'MonthYear'] : [];
                  const defaultVariant = selectedType === 'DateTime' ? 'DateTime' : 'Date';

                  setValue(
                    'dateFormatVariant',
                    allowedVariants.length === 0 ? null : allowedVariants.includes(dateFormatVariant ?? '') ? dateFormatVariant : defaultVariant
                  );
                  if (selectedType !== 'MultiSelect') setValue('functionParameterSelectedList', []);
                  if (selectedType !== 'Relation') {
                    setValue('isMultiSelect', false);
                    setValue('templateBrowseId', null);
                  }
                }}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                fullWidth
                label={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.type`)}>
                {parameterTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.types.${option}`)}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          <Controller
            name="order"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                type="number"
                fullWidth
                label={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.order`)}
              />
            )}
          />

          {(functionParameterType === 'Date' || functionParameterType === 'DateTime') && (
            <Controller
              name="dateFormatVariant"
              control={control}
              render={({ field, fieldState }) => {
                const dateFormatOptions = functionParameterType === 'Date' ? ['Date', 'MonthYear'] : ['DateTime', 'Date', 'MonthYear'];

                return (
                  <Select
                    {...field}
                    value={field.value ?? (functionParameterType === 'Date' ? 'Date' : 'DateTime')}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    fullWidth
                    label={t('helios.templates.forms.item.formatting.dateTime.label')}>
                    {dateFormatOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {t(`helios.templates.forms.item.formatting.dateTime.${option === 'MonthYear' ? 'monthYear' : option.toLowerCase()}`)}
                      </MenuItem>
                    ))}
                  </Select>
                );
              }}
            />
          )}

          {functionParameterType === 'MultiSelect' && (
            <>
              <SectionTitle variant="caption">{t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.options`)}</SectionTitle>
              <Box sx={{ gridColumn: '1 / -1' }}>
                {fields.map((item, index) => (
                  <FieldsValues key={item.id}>
                    <Controller
                      name={`functionParameterSelectedList.${index}.name`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          label={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.key`)}
                        />
                      )}
                    />
                    <Controller
                      name={`functionParameterSelectedList.${index}.value`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          label={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.value`)}
                        />
                      )}
                    />
                    <div>
                      <IconButton size="small" onClick={() => remove(index)} sx={{ marginTop: '25px' }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </div>
                  </FieldsValues>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button size="small" variant="contained" onClick={handleAddOption}>
                    <Add /> {t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.buttons.addOption`)}
                  </Button>
                </Box>
              </Box>
            </>
          )}

          {functionParameterType === 'Relation' && (
            <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <LookupInput
                label={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.overviewSelect`)}
                placeholder={t('common.selectValue')}
                value={templateBrowseId ? templateData?.name || '' : ''}
                onClick={toggleOverviewTemplateOpen}
                onClear={() => setValue('templateBrowseId', null)}
                background="white"
                fullWidth
                error={Boolean(errors.templateBrowseId)}
                helperText={errors.templateBrowseId?.message}
              />
              <Controller
                name="isMultiSelect"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value ?? false}
                    onChange={(_e, checked) => field.onChange(checked)}
                    slotProps={{ input: { ref: field.ref } }}
                    name={field.name}
                    label={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.isMultiSelect`)}
                  />
                )}
              />
            </Box>
          )}

          <SectionTitle variant="caption">{t('helios.functions.addFunctionModal.fields.sections.settings')}</SectionTitle>

          <Controller
            name="isRequired"
            control={control}
            render={({ field }) => (
              <Switch
                checked={field.value}
                onChange={(_e, checked) => field.onChange(checked)}
                slotProps={{ input: { ref: field.ref } }}
                name={field.name}
                label={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.isRequired`)}
              />
            )}
          />
        </FormWrapper>
      </ActionModal>
      <SelectOverviewTemplateModal
        datasourceId={datasourceId}
        defaultSelectedId={templateBrowseId}
        open={isOverviewTemplateOpen}
        onSubmit={onSelectOverviewTemplate}
        onClose={toggleOverviewTemplateOpen}
      />
    </>
  );
};
